import { Box, Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import type { AuthType } from '../domain/vendor';
import type { AuthProfileKind } from '../lib/authProfileKind';

export interface CapabilityAuthConnectPanelProps {
  authType: AuthType;
  authConfig: Record<string, string>;
  authProfileKind: AuthProfileKind;
  onAuthTypeChange: (authType: AuthType) => void;
  onAuthChange: (field: string, value: string | boolean) => void;
  secretInputValue: (value: string | undefined) => string;
  secretInputType: (value: string | undefined) => string;
  isMaskedSecretValue: (value: string | undefined) => boolean;
  isTokenPreflightEnabled: (authConfig: Record<string, string>) => boolean;
}

function TokenPreflightFields({
  authConfig,
  setAuth,
  isTokenPreflightEnabled,
}: {
  authConfig: Record<string, string>;
  setAuth: (field: string, value: string | boolean) => void;
  isTokenPreflightEnabled: (authConfig: Record<string, string>) => boolean;
}) {
  return (
    <>
      <FormControlLabel
        sx={{ gridColumn: { md: '1 / -1' } }}
        control={
          <Checkbox
            checked={isTokenPreflightEnabled(authConfig)}
            onChange={e => setAuth('tokenPreflightEnabled', e.target.checked ? 'true' : 'false')}
          />
        }
        label="Use token preflight before this API request"
      />
      <TextField
        label="Token endpoint path"
        value={authConfig.tokenPath ?? ''}
        onChange={e => setAuth('tokenPath', e.target.value)}
        placeholder="/BankingServices/GenerateAuthorizeToken"
        fullWidth
        sx={{ gridColumn: { md: '1 / -1' } }}
      />
      <TextField
        label="Token response field"
        value={authConfig.tokenResponsePath ?? ''}
        onChange={e => setAuth('tokenResponsePath', e.target.value)}
        placeholder="Token"
      />
      <TextField
        label="Token target header"
        value={authConfig.tokenTargetHeaderName ?? ''}
        onChange={e => setAuth('tokenTargetHeaderName', e.target.value)}
        placeholder={authConfig.headerName || authConfig.accountsApiKeyHeaderName || 'flinks-auth-key'}
      />
    </>
  );
}

export function CapabilityAuthConnectPanel({
  authType,
  authConfig,
  authProfileKind,
  onAuthTypeChange,
  onAuthChange,
  secretInputValue,
  secretInputType,
  isMaskedSecretValue,
  isTokenPreflightEnabled,
}: CapabilityAuthConnectPanelProps) {
  const setAuth = (field: string, value: string | boolean) => onAuthChange(field, value);

  if (authProfileKind === 'accounts') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <FormControl fullWidth>
          <InputLabel>Auth Type</InputLabel>
          <Select
            value="api-key"
            label="Auth Type"
            onChange={() => onAuthTypeChange('api-key')}
          >
            <MenuItem value="api-key">API Key Header</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          <TextField
            label="API key header name"
            value={authConfig.accountsApiKeyHeaderName ?? ''}
            onChange={e => setAuth('accountsApiKeyHeaderName', e.target.value)}
            placeholder="x-api-key"
          />
          <TextField
            label="API key env var"
            value={authConfig.accountsApiKeyEnv ?? authConfig.flinksApiKeyEnv ?? ''}
            onChange={e => setAuth('accountsApiKeyEnv', e.target.value)}
            placeholder="FLINKS_API_KEY"
            helperText="Resolved at runtime for this API only."
          />
          <TextField
            label="API key (inline)"
            value={secretInputValue(authConfig.accountsApiKey ?? authConfig.flinksApiKey)}
            onChange={e => setAuth('accountsApiKey', e.target.value)}
            helperText={
              isMaskedSecretValue(authConfig.accountsApiKey ?? authConfig.flinksApiKey)
                ? 'Saved secret is preserved unless replaced.'
                : 'Optional when env var is set.'
            }
            type={secretInputType(authConfig.accountsApiKey ?? authConfig.flinksApiKey)}
            sx={{ gridColumn: { md: '1 / -1' } }}
          />
          <TokenPreflightFields
            authConfig={authConfig}
            setAuth={setAuth}
            isTokenPreflightEnabled={isTokenPreflightEnabled}
          />
        </Box>
      </Box>
    );
  }

  return (
    <>
      <FormControl fullWidth>
        <InputLabel>Auth Type</InputLabel>
        <Select
          value={authType}
          label="Auth Type"
          onChange={e => onAuthTypeChange(e.target.value as AuthType)}
        >
          <MenuItem value="none">None</MenuItem>
          <MenuItem value="api-key">API Key Header</MenuItem>
          <MenuItem value="bearer">Bearer Token</MenuItem>
          <MenuItem value="basic">Basic Auth</MenuItem>
          <MenuItem value="oauth-client-credentials">OAuth Client Credentials</MenuItem>
        </Select>
      </FormControl>

      {authType === 'api-key' && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 2fr' }, gap: 2 }}>
          <TextField
            label="Header name"
            value={authConfig.headerName ?? ''}
            onChange={e => setAuth('headerName', e.target.value)}
            placeholder="x-api-key"
          />
          <TextField
            label="API key env var"
            value={authConfig.apiKeyEnv ?? ''}
            onChange={e => setAuth('apiKeyEnv', e.target.value)}
            placeholder="FLINKS_AUTH_KEY"
            helperText="Used for this API."
          />
          <TextField
            label="API key (inline)"
            value={secretInputValue(authConfig.apiKey ?? authConfig.key)}
            onChange={e => setAuth('apiKey', e.target.value)}
            helperText={
              isMaskedSecretValue(authConfig.apiKey ?? authConfig.key)
                ? 'Saved secret is preserved unless replaced.'
                : 'Optional inline key for connection and live test.'
            }
            type={secretInputType(authConfig.apiKey ?? authConfig.key)}
            sx={{ gridColumn: { md: '1 / -1' } }}
          />
          <TokenPreflightFields
            authConfig={authConfig}
            setAuth={setAuth}
            isTokenPreflightEnabled={isTokenPreflightEnabled}
          />
        </Box>
      )}

      {authType === 'bearer' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Token env var"
            value={authConfig.tokenEnv ?? ''}
            onChange={e => setAuth('tokenEnv', e.target.value)}
            placeholder="OPENAI_API_KEY"
            fullWidth
          />
          <TextField
            label="Bearer token"
            value={secretInputValue(authConfig.token)}
            onChange={e => setAuth('token', e.target.value)}
            type={secretInputType(authConfig.token)}
            fullWidth
          />
        </Box>
      )}

      {authType === 'basic' && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          <TextField label="Username" value={authConfig.username ?? ''} onChange={e => setAuth('username', e.target.value)} />
          <TextField
            label="Password"
            value={secretInputValue(authConfig.password)}
            onChange={e => setAuth('password', e.target.value)}
            type={secretInputType(authConfig.password)}
          />
        </Box>
      )}

      {authType === 'oauth-client-credentials' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField label="Token URL" value={authConfig.tokenUrl ?? ''} onChange={e => setAuth('tokenUrl', e.target.value)} fullWidth />
          <TextField label="Client ID" value={authConfig.clientId ?? ''} onChange={e => setAuth('clientId', e.target.value)} fullWidth />
          <TextField
            label="Client secret"
            value={secretInputValue(authConfig.clientSecret)}
            onChange={e => setAuth('clientSecret', e.target.value)}
            type={secretInputType(authConfig.clientSecret)}
            fullWidth
          />
          <TextField label="Scope" value={authConfig.scope ?? ''} onChange={e => setAuth('scope', e.target.value)} fullWidth />
        </Box>
      )}
    </>
  );
}
